import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Typography,
  Stack,
} from "@mui/material";
import QRCode from "qrcode";
import { useAppSelector } from "hooks/use-app-select";
import { useEffect, useState } from "react";
import history from "utils/history";
import AuthLayout from "layouts/AuthLayout/AuthLayout";
import {
  activateMFA,
  fetchPreAuthSecretKey,
  generateQRCodeForMFA,
} from "screens/Auth/AuthService";
import { MuiOtpInput } from "mui-one-time-password-input";
import {
  convertResourceToObjectFormat,
  handleErrorMessage,
  isTruthy,
  isValidOTP,
  openErrorNotification,
} from "helpers/methods";
import { loginAction, selectAuthenticated } from "redux/authSlice";
import { getAccountDisplayName } from "./authSession";
import { useAuthRouteRedirection } from "layouts/Config";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import { useAppDispatch } from "hooks/use-app-dispatch";
import defaultImage from "assets/icons1/avatars/default.png";
import { updatePreference } from "redux/preferencesSlice";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import inkfreeLogo from "assets/images/side_nav_inkfree_logo.svg";
import { mobile, signer, signerAccentBar } from "theme/colors";
import viewpaths from "global/constants/ViewPathConstants";
import { authRef, refFont } from "./AuthReferenceStyles";
import { useLocation } from "react-router-dom";

interface CustomProps {
  location: any;
}

const SetupMFA = (props: CustomProps) => {
  useTitle(title.SETUP_MFA);
  const dispatch = useAppDispatch();
  const email = props.location.state?.email ?? "";
  const password = props.location.state?.password ?? "";
  const token = props.location.state?.token ?? "";
  const isLoggedIn = useAppSelector(selectAuthenticated);
  const authRoute = useAuthRouteRedirection();
  const redirectionParam = new URLSearchParams(useLocation().search).get(
    "redirectionUrl",
  );
  const decodedUrl = redirectionParam
    ? decodeURIComponent(redirectionParam)
    : undefined;
  const [payload, setPayload] = useState({
    email: email,
    code: "",
    password: password,
  });
  const [codeError, setCodeError] = useState<string>();
  const [processing, setProcessing] = useState<boolean>(false);
  const [qr, setQR] = useState<string>();
  const [keyCopied, setKeyCopied] = useState<boolean>(false);
  const [preAuthSecretKey, setPreAuthSecretKey] = useState<string>("");

  useEffect(() => {
    if (isTruthy(token)) {
      makeQRCode();
      getPreAuthSecretKey();
    } else {
      history.push(viewpaths.loginViewPath);
    }
  }, []);

  const makeQRCode = async () => {
    try {
      const response = await generateQRCodeForMFA(token);
      await generateQRCodeImage(response.message);
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  const getPreAuthSecretKey = async () => {
    try {
      const key = await fetchPreAuthSecretKey(token);
      setPreAuthSecretKey(`${key?.match(/.{1,6}/g).join("-")}`);
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  const verifyCode = async () => {
    try {
      if (!isValidOTP(payload.code)) {
        setCodeError("Please enter a valid 6-digit code");
        return openErrorNotification("Please enter the code");
      }
      setProcessing(true);
      const user = await activateMFA(
        token,
        payload.email,
        payload.password,
        payload.code,
      );
      const formattedResources = convertResourceToObjectFormat(user.resources);
      if (isTruthy(formattedResources)) {
        dispatch(
          loginAction({
            authenticated: true,
            accessToken: user.authToken,
            refreshToken: user.refreshToken,
            userName: user.name,
            email: (user.email ?? "").toLowerCase(),
            userRole: user.role,
            resources: formattedResources,
            profileImage: user.profilePicture
              ? "data:image/png;base64," + user.profilePicture
              : defaultImage,
            userId: user.id,
            account: user.account,
            accountDisplayName: getAccountDisplayName(user),
            isTrial: user.trial,
          }),
        );
        dispatch(
          updatePreference({
            layout: "vertical",
            theme: "dark",
          }),
        );
      } else {
        openErrorNotification("Something went wrong.");
      }
      setProcessing(false);
    } catch (error: any) {
      setProcessing(false);
      openErrorNotification(error.message);
    }
  };

  const generateQRCodeImage = async (url: string) => {
    try {
      const qrCodeImage = await QRCode.toDataURL(url);
      setQR(qrCodeImage);
    } catch (error: any) {
      openErrorNotification(error);
    }
  };

  const handleKeypress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      verifyCode();
    }
  };

  const copyToClipBoard = async () => {
    await navigator.clipboard.writeText(preAuthSecretKey);
    setKeyCopied(true);
    setTimeout(() => setKeyCopied(false), 3000);
  };

  const StepNumber = ({ number }: { number: number }) => (
    <Box
      aria-hidden="true"
      sx={{
        width: 22,
        height: 22,
        borderRadius: "50%",
        backgroundColor: mobile.base[500],
        color: "common.white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: refFont.legal,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {number}
    </Box>
  );

  const stepCopy = (text: string) => (
    <Typography
      sx={{
        color: authRef.stepText,
        fontSize: refFont.label,
        lineHeight: 1.6,
        pt: "2px",
      }}
    >
      {text}
    </Typography>
  );

  const renderSetupMFAScreen = () => {
    return (
      <Box>
        {/* Brand accent bar across the top of the canvas, per reference 3f. */}
        <Box
          aria-hidden="true"
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            height: "5px",
            background: signerAccentBar,
            zIndex: (theme) => theme.zIndex.appBar,
          }}
        />
        <Card
          elevation={0}
          sx={{
            width: "100%",
            maxWidth: 620,
            mx: "auto",
            borderRadius: "16px",
            border: `1px solid ${authRef.cardBorder}`,
            boxShadow: authRef.cardShadow,
            padding: { xs: "28px 24px 24px", sm: "36px 48px 34px" },
            boxSizing: "border-box",
          }}
        >
          <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
            <Box
              component="img"
              src={inkfreeLogo}
              alt="Inkfree e-Sign"
              sx={{ width: 106, height: "auto", display: "block", mx: "auto" }}
            />
            <Typography
              component="h1"
              sx={{
                mt: 2.5,
                color: mobile.base[500],
                fontSize: refFont.cardHeading,
                fontWeight: 700,
                textAlign: "center",
              }}
            >
              Secure your account
            </Typography>

            {/* Admin-policy banner */}
            <Box
              sx={{
                display: "flex",
                gap: 1.25,
                alignItems: "flex-start",
                mt: 2,
                padding: "12px 16px",
                backgroundColor: signer.purple.tint,
                border: `1px solid ${signer.purple.border}`,
                borderRadius: "10px",
              }}
            >
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                fill="none"
                stroke={signer.purple.main}
                strokeWidth="1.8"
                style={{ width: 18, height: 18, flexShrink: 0, marginTop: 1 }}
              >
                <path d="M12 3l7 3v5c0 4.5-3 8.2-7 9.5-4-1.3-7-5-7-9.5V6l7-3z" />
                <path d="M9.5 12l2 2 3.5-3.5" />
              </svg>
              <Typography
                sx={{
                  color: authRef.noticeTextPurple,
                  fontSize: refFont.link,
                  lineHeight: 1.6,
                }}
              >
                <Box component="span" sx={{ fontWeight: 700 }}>
                  Your account admin requires two-factor authentication
                </Box>{" "}
                for everyone in your organization. It takes about a minute to
                set up.
              </Typography>
            </Box>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={3.5}
              alignItems={{ xs: "center", sm: "flex-start" }}
              sx={{ mt: 3.25 }}
            >
              {/* Left column: QR code */}
              <Stack spacing={1.25} sx={{ flexShrink: 0, alignItems: "center" }}>
                <Box
                  sx={{
                    width: 172,
                    height: 172,
                    border: `1px solid ${authRef.chipBorder}`,
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  <Box
                    component="img"
                    src={qr}
                    alt="QR code for multi-factor authentication setup"
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    color: authRef.metaText,
                    fontSize: refFont.cardFooter,
                    textAlign: "center",
                  }}
                >
                  Can't scan? Use the key &rarr;
                </Typography>
              </Stack>

              {/* Right column: steps */}
              <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
                <Stack direction="row" spacing={1.25} alignItems="flex-start">
                  <StepNumber number={1} />
                  {stepCopy(
                    "Install an authenticator app — Google Authenticator, Authy, or 1Password.",
                  )}
                </Stack>

                <Stack
                  direction="row"
                  spacing={1.25}
                  alignItems="flex-start"
                  sx={{ mt: 1.75 }}
                >
                  <StepNumber number={2} />
                  {stepCopy("Scan the QR code, or enter this key manually:")}
                </Stack>
                {preAuthSecretKey && (
                  <Box
                    sx={{
                      // Indented to sit under the step copy, not the numeral.
                      ml: "32px",
                      mt: 1.25,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      padding: "10px 14px",
                      backgroundColor: authRef.inputBg,
                      border: `1px solid ${authRef.chipBorder}`,
                      borderRadius: "8px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "ui-monospace, Menlo, monospace",
                        fontWeight: 700,
                        fontSize: refFont.cardBody,
                        letterSpacing: "0.06em",
                        color: mobile.base[500],
                        overflowWrap: "anywhere",
                      }}
                    >
                      {preAuthSecretKey}
                    </Typography>
                    <Button
                      onClick={copyToClipBoard}
                      size="small"
                      startIcon={
                        keyCopied ? (
                          <DoneAllIcon sx={{ fontSize: "14px" }} />
                        ) : (
                          <ContentCopyIcon sx={{ fontSize: "14px" }} />
                        )
                      }
                      aria-label="Copy secret key to clipboard"
                      sx={{
                        ml: "auto",
                        // Reserved so the label swap Copy -> Copied doesn't shift the chip.
                        minWidth: "68px",
                        justifyContent: "flex-end",
                        flexShrink: 0,
                        p: 0,
                        color: signer.orange.main,
                        fontSize: refFont.cardMeta,
                        fontWeight: 600,
                        lineHeight: 1,
                        whiteSpace: "nowrap",
                        textTransform: "none",
                        "& .MuiButton-startIcon": { ml: 0, mr: 0.5 },
                        "&:hover": {
                          backgroundColor: "transparent",
                          color: signer.orange.dark,
                        },
                      }}
                    >
                      {keyCopied ? "Copied" : "Copy"}
                    </Button>
                  </Box>
                )}

                <Stack
                  direction="row"
                  spacing={1.25}
                  alignItems="flex-start"
                  sx={{ mt: 2 }}
                >
                  <StepNumber number={3} />
                  {stepCopy("Enter the 6-digit code from the app:")}
                </Stack>
                <FormControl sx={{ width: "100%", ml: "32px", mt: 1.5, pr: "32px" }}>
                  <MuiOtpInput
                    length={6}
                    onChange={(value: any) => {
                      setPayload({
                        ...payload,
                        code: value.replace(/[^0-9]/g, ""),
                      });
                      setCodeError("");
                    }}
                    sx={{
                      gap: 1,
                      "& .MuiInputBase-root": {
                        width: "100%",
                        maxWidth: "40px",
                        minWidth: "30px",
                        height: "48px",
                        borderRadius: "8px",
                        backgroundColor: authRef.inputBg,
                        "& fieldset": {
                          borderColor: authRef.inputBorder,
                          borderWidth: "1px",
                        },
                        "&:hover fieldset": {
                          borderColor: authRef.inputBorderHover,
                        },
                        // Active cell in the reference: white fill, orange rule.
                        "&.Mui-focused": {
                          backgroundColor: "background.paper",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: signer.orange.main,
                          borderWidth: "1.5px",
                        },
                      },
                      "& .MuiInputBase-input": {
                        padding: 0,
                        fontSize: "18px",
                        fontWeight: 700,
                        color: mobile.base[500],
                      },
                    }}
                    value={payload.code}
                    onKeyPress={handleKeypress}
                    TextFieldsProps={{
                      variant: "outlined",
                      type: "number",
                      inputProps: {
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                        style: {
                          MozAppearance: "textfield",
                        },
                        "aria-required": true,
                        "aria-label": "One time password input",
                        "aria-describedby": codeError ? "otp-error" : undefined,
                        "aria-invalid": !!codeError,
                      },
                      sx: {
                        "& input[type=number]": {
                          MozAppearance: "textfield",
                        },
                        "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                          {
                            WebkitAppearance: "none",
                            margin: 0,
                          },
                      },
                    }}
                  />
                  {isTruthy(codeError) && (
                    <Typography
                      id="otp-error"
                      role="alert"
                      variant="caption"
                      color="error.main"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      Please enter the 6-digit code
                    </Typography>
                  )}
                </FormControl>
              </Box>
            </Stack>

            <Button
              fullWidth
              size="large"
              type="submit"
              variant="contained"
              onClick={verifyCode}
              disabled={processing}
              sx={{
                mt: 3.5,
                height: "48px",
                borderRadius: "8px",
                fontWeight: 700,
                fontSize: refFont.cardButton,
                letterSpacing: 0,
                textTransform: "none",
                "&:focus-visible": {
                  outline: "2px solid",
                  outlineColor: "primary.main",
                  outlineOffset: "2px",
                },
              }}
              aria-label={
                processing
                  ? "Verifying, please wait"
                  : "Verify and enable two factor authentication"
              }
            >
              {processing ? "Verifying..." : "Verify & enable 2FA"}
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  };

  if (isLoggedIn) {
    history.push(decodedUrl ?? authRoute);
    return null;
  } else {
    return <AuthLayout>{renderSetupMFAScreen()}</AuthLayout>;
  }
};

export default SetupMFA;
